import { useEffect, useState } from "react";
import { useRouter } from "next/router";

import { matchAutoTour } from "@/components/tour/matchAutoTour";
import { startTour } from "@/components/tour/TourGuide";
import { AutoTourEntry, getAutoTourRoutes } from "@/controllers/tutorials";

/**
 * The guided tour belonging to the page currently on screen, if there is one.
 *
 * Tours reach a page by two routes. A handful are mounted by the page itself
 * with `useTour()`, which hands that page a `start()` it can put behind a
 * button. The rest — the large majority — are matched to a route by
 * `GlobalTourLauncher` from the server's auto-route map, and the page they
 * open on never learns they exist.
 *
 * That asymmetry is why most pages offered no way to replay their tour: not
 * because they had none, but because they had no handle on it. Worst affected
 * were the stock tabs, which cannot appear in the Help menu either — those
 * links need a concrete ticker — so their tour auto-showed once and was then
 * unreachable for good.
 *
 * `ContentLayout` calls this for every page, so the "Restart the guided tour"
 * action appears wherever a tour matches the route. Adding a tour in the admin
 * is enough for its page to offer it; no front-end change has to follow a tour
 * around.
 *
 * `start()` goes through `startTour()`, which deliberately bypasses the
 * per-user dismissal record — dismissal suppresses the *automatic* opening,
 * never an explicit ask.
 */

/**
 * One fetch per page load at most, shared by every layout on screen.
 *
 * The answer is identical for every visitor and is cached both server-side and
 * in the browser, but several mounted layouts asking at once would still be
 * several requests. A failed fetch clears the handle so a later mount retries
 * rather than caching the failure for the session.
 */
let inflight: Promise<AutoTourEntry[]> | null = null;

export const loadAutoTours = (): Promise<AutoTourEntry[]> => {
    if (!inflight) {
        inflight = getAutoTourRoutes().catch(() => {
            inflight = null;
            return [];
        });
    }
    return inflight;
};

export interface PageTour {
    tourId: string;
    start: () => void;
}

export const usePageTour = (): PageTour | undefined => {
    const router = useRouter();
    const [entries, setEntries] = useState<AutoTourEntry[]>([]);

    useEffect(() => {
        let cancelled = false;
        loadAutoTours().then((auto) => {
            if (!cancelled) setEntries(auto);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!router.isReady || !entries.length) return undefined;

    const match = matchAutoTour(entries, router.pathname, router.asPath);
    if (!match) return undefined;

    return { tourId: match.id, start: () => startTour(match.id) };
};
